import { create } from 'zustand';

/**
 * aiPanelStore — 우측 AI 패널의 **표시 상태** (설계 판 A · 정의서 P1·P2·P6·P7).
 *
 * 메모 패널(`memoPanelStore`)과 **같은 계약**이다: 도킹·폭은 취향이라 기기에 남기고,
 * **열림 여부는 남기지 않는다** — 새로고침 때 패널이 저절로 떠 있으면 놀란다.
 *
 * ★★★ **도킹은 «둘»이다** — 사이드바(`inline`) · 플로팅(`overlay`, 기본).
 *    시안 초안에 있던 「전체」는 **도킹이 아니라 이동**이다(2026-09-09 사용자 결정) —
 *    패널을 크게 펼치는 것이 아니라 닫고 AI 메뉴 화면으로 나간다. 그래서 여기에 상태 축이
 *    늘지 않는다(문서 패널의 `expanded` 축을 빌리지 않은 이유).
 *
 * ★ 폭은 메모(360/300/640)보다 한 단 넓다 — 도구 결과 표가 들어가기 때문이다.
 */

export const AI_PANEL_MIN_WIDTH = 360;
export const AI_PANEL_MAX_WIDTH = 720;
export const AI_PANEL_DEFAULT_WIDTH = 400;

const LS_WIDTH = 'ai.panel.width';
const LS_DOCK = 'ai.panel.dock';

export type AiDock = 'overlay' | 'inline';

function clampWidth(w: number): number {
  return Math.min(AI_PANEL_MAX_WIDTH, Math.max(AI_PANEL_MIN_WIDTH, Math.round(w)));
}

function readWidth(): number {
  try {
    const v = Number(localStorage.getItem(LS_WIDTH));
    return Number.isFinite(v) && v > 0 ? clampWidth(v) : AI_PANEL_DEFAULT_WIDTH;
  } catch {
    return AI_PANEL_DEFAULT_WIDTH;
  }
}

function readDock(): AiDock {
  try {
    // 기본은 플로팅(정의서 P6) — 본문을 밀지 않는다.
    return localStorage.getItem(LS_DOCK) === 'inline' ? 'inline' : 'overlay';
  } catch {
    return 'overlay';
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage 비활성(프라이빗 모드 등) — 세션 내 동작은 유지 */
  }
}

interface AiPanelState {
  open: boolean;
  dock: AiDock;
  width: number;

  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setDock: (d: AiDock) => void;
  setWidth: (w: number) => void;
}

export const useAiPanelStore = create<AiPanelState>((set) => ({
  open: false,
  dock: readDock(),
  width: readWidth(),

  openPanel: () => set({ open: true }),
  closePanel: () => set({ open: false }),
  togglePanel: () => set((s) => ({ open: !s.open })),

  /**
   * 도킹은 **고르는 것**이지 순환시키는 것이 아니다 — 목록에서 체크가 붙는 상태라
   * `toggleDock` 이 아니라 값을 받는다(정의서 P6).
   */
  setDock: (d) => {
    persist(LS_DOCK, d);
    set({ dock: d });
  },
  setWidth: (w) => {
    const next = clampWidth(w);
    persist(LS_WIDTH, String(next));
    set({ width: next });
  },
}));
